import { useState, useRef, useEffect } from 'react';
import { getSiteConfig, saveSiteConfig, resetSiteConfig } from '@/utils/siteConfigStore';
import { defaultSiteConfig, type SiteConfig } from '@/mocks/siteConfigData';
import { uploadImage } from '@/utils/apiClient';

type ConfigTab = 'global' | 'stats' | 'contact' | 'modal' | 'footer' | 'culture';

function ImageUploader({ label, value, onChange, hint, specHint }: { label: string; value: string; onChange: (v: string) => void; hint?: string; specHint?: string }) {
  const [preview, setPreview] = useState(value);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => { setPreview(value); }, [value]);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setUploadError('');

    try {
      const publicUrl = await uploadImage(file);
      setPreview(publicUrl);
      onChange(publicUrl);
    } catch (err) {
      const message = err instanceof Error ? err.message : '上传失败';
      setUploadError(message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="bg-white rounded-xl border border-brand-light/40 p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-medium text-brand-deep">{label}</span>
        {hint && <span className="text-xs text-gray-400">{hint}</span>}
      </div>
      <div className="flex items-start gap-5">
        <div className="w-28 h-28 md:w-32 md:h-32 bg-brand-light/60 rounded-lg overflow-hidden flex items-center justify-center flex-shrink-0 border border-brand-light/30">
          {preview ? (
            <img src={preview} alt={label} className="w-full h-full object-contain p-2" />
          ) : (
            <i className="ri-image-line text-gray-300 text-2xl" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-col gap-2">
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-brand-light text-brand-dark text-xs font-medium rounded-lg hover:bg-brand-cream transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              <i className={`${uploading ? 'ri-loader-4-line animate-spin' : 'ri-upload-cloud-2-line'}`} />
              {uploading ? '上传中...' : '上传图片'}
            </button>
            <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" />
            {uploadError && (
              <p className="text-xs text-red-500 flex items-center gap-1">
                <i className="ri-error-warning-line" />
                {uploadError}
              </p>
            )}
            <div className="relative">
              <input
                type="text"
                value={value}
                onChange={(e) => { onChange(e.target.value); setPreview(e.target.value); }}
                placeholder="或粘贴图片 URL"
                className="w-full px-3 py-1.5 text-xs text-gray-500 bg-brand-gray/50 rounded-md border border-brand-light/30 focus:outline-none focus:border-brand-brown/30"
              />
            </div>
          </div>
          <p className="text-[10px] text-gray-400 mt-2">上传图片将自动存储至云端，发布后仍可正常显示</p>
          {specHint && (
            <div className="mt-2 p-2.5 bg-amber-50/60 rounded-lg border border-amber-100/60">
              <p className="text-[10px] font-medium text-amber-700 mb-1">
                <i className="ri-information-line mr-0.5" />
                建议规格
              </p>
              <p className="text-[10px] text-amber-600">{specHint}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatsEditor({ stats, onChange, title }: { stats: { value: string; label: string }[]; onChange: (s: { value: string; label: string }[]) => void; title: string }) {
  const handleChange = (index: number, field: 'value' | 'label', val: string) => {
    const next = stats.map((s, i) => (i === index ? { ...s, [field]: val } : s));
    onChange(next);
  };

  return (
    <div className="bg-white rounded-xl border border-brand-light/40 p-5">
      <h4 className="text-sm font-semibold text-brand-deep mb-4">{title}</h4>
      <div className="grid grid-cols-2 gap-3">
        {stats.map((stat, i) => (
          <div key={i} className="flex gap-2">
            <input
              type="text"
              value={stat.value}
              onChange={(e) => handleChange(i, 'value', e.target.value)}
              placeholder="数值"
              className="w-20 px-3 py-2 text-sm text-brand-deep bg-brand-gray/50 rounded-lg border border-brand-light/30 focus:outline-none focus:border-brand-brown/40 text-center font-bold"
            />
            <input
              type="text"
              value={stat.label}
              onChange={(e) => handleChange(i, 'label', e.target.value)}
              placeholder="标签"
              className="flex-1 px-3 py-2 text-sm text-gray-500 bg-brand-gray/50 rounded-lg border border-brand-light/30 focus:outline-none focus:border-brand-brown/40"
            />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function SiteConfigManagement() {
  const [config, setConfig] = useState<SiteConfig>(getSiteConfig);
  const [activeTab, setActiveTab] = useState<ConfigTab>('global');
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const update = (partial: Partial<SiteConfig>) => {
    const next = { ...config, ...partial };
    setConfig(next);
  };

  const handleSave = async () => {
    setSaving(true);
    const ok = await saveSiteConfig(config);
    setSaving(false);
    if (ok) {
      setSaved(true);
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => setSaved(false), 2000);
    }
  };

  const handleReset = async () => {
    const reset = await resetSiteConfig();
    setConfig(reset);
  };

  const tabs: { key: ConfigTab; icon: string; label: string }[] = [
    { key: 'global', icon: 'ri-image-line', label: '全局 Logo' },
    { key: 'stats', icon: 'ri-bar-chart-line', label: '数据统计' },
    { key: 'contact', icon: 'ri-contacts-line', label: '联系我们' },
    { key: 'modal', icon: 'ri-window-line', label: '弹窗配置' },
    { key: 'footer', icon: 'ri-layout-bottom-line', label: '底部栏' },
    { key: 'culture', icon: 'ri-heart-pulse-line', label: '企业文化' },
  ];

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-lg font-bold text-brand-deep">基础数值配置</h2>
          <p className="text-xs text-gray-400 mt-1">修改站点全局展示数据，保存后官网即时生效</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleReset}
            className="px-4 py-2 text-xs font-medium text-gray-400 hover:text-brand-deep hover:bg-brand-light rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-restart-line mr-1" />
            恢复默认
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className={`px-5 py-2 text-xs font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap disabled:opacity-60 ${
              saved
                ? 'bg-green-50 text-green-600'
                : 'bg-brand-deep text-white hover:bg-brand-dark'
            }`}
          >
            <i className={`${saving ? 'ri-loader-4-line animate-spin' : saved ? 'ri-check-line' : 'ri-save-line'} mr-1`} />
            {saving ? '保存中...' : saved ? '已保存' : '保存配置'}
          </button>
        </div>
      </div>

      <div className="flex items-center gap-1 mb-6 overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-full transition-colors cursor-pointer whitespace-nowrap ${
              activeTab === tab.key
                ? 'bg-brand-deep text-white'
                : 'text-gray-400 hover:text-brand-deep hover:bg-brand-light'
            }`}
          >
            <i className={tab.icon} />
            {tab.label}
          </button>
        ))}
      </div>

      <div className="space-y-5">
        {activeTab === 'global' && (
          <div className="space-y-5">
            <ImageUploader
              label="网站 Logo（浅色版）"
              value={config.logoUrl}
              onChange={(v) => update({ logoUrl: v })}
              hint="显示于导航栏顶部（深色背景时）及底部栏"
              specHint="尺寸：400 × 120 px（2x 高清）· 72 DPI · PNG 透明背景 · 不超过 2MB"
            />
            <ImageUploader
              label="网站 Logo（深色版）"
              value={config.logoUrlDark}
              onChange={(v) => update({ logoUrlDark: v })}
              hint="显示于导航栏滚动后（白色背景时）"
              specHint="尺寸：400 × 120 px（2x 高清）· 72 DPI · PNG 透明背景 · 不超过 2MB"
            />
          </div>
        )}

        {activeTab === 'stats' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <StatsEditor
              title="首页 — 品牌实力数据"
              stats={config.brandStats}
              onChange={(s) => update({ brandStats: s })}
            />
            <StatsEditor
              title="招商加盟页 — 品牌数据"
              stats={config.franchiseStats}
              onChange={(s) => update({ franchiseStats: s })}
            />
          </div>
        )}

        {activeTab === 'contact' && (
          <div className="space-y-5">
            <div className="bg-white rounded-xl border border-brand-light/40 p-5">
              <h4 className="text-sm font-semibold text-brand-deep mb-4">总部联系方式</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { key: 'company' as const, label: '公司名称' },
                  { key: 'brand' as const, label: '品牌名称' },
                  { key: 'phone' as const, label: '联系电话' },
                  { key: 'email' as const, label: '电子邮箱' },
                ].map(({ key, label }) => (
                  <div key={key}>
                    <label className="block text-xs text-gray-400 mb-1.5">{label}</label>
                    <input
                      type="text"
                      value={config.contactInfo[key]}
                      onChange={(e) => update({ contactInfo: { ...config.contactInfo, [key]: e.target.value } })}
                      className="w-full px-3 py-2 text-sm text-brand-deep bg-brand-gray/50 rounded-lg border border-brand-light/30 focus:outline-none focus:border-brand-brown/40"
                    />
                  </div>
                ))}
                <div className="md:col-span-2">
                  <label className="block text-xs text-gray-400 mb-1.5">总部地址</label>
                  <input
                    type="text"
                    value={config.contactInfo.address}
                    onChange={(e) => update({ contactInfo: { ...config.contactInfo, address: e.target.value } })}
                    className="w-full px-3 py-2 text-sm text-brand-deep bg-brand-gray/50 rounded-lg border border-brand-light/30 focus:outline-none focus:border-brand-brown/40"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1.5">营业时间</label>
                  <input
                    type="text"
                    value={config.contactInfo.workingHours}
                    onChange={(e) => update({ contactInfo: { ...config.contactInfo, workingHours: e.target.value } })}
                    className="w-full px-3 py-2 text-sm text-brand-deep bg-brand-gray/50 rounded-lg border border-brand-light/30 focus:outline-none focus:border-brand-brown/40"
                  />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-brand-light/40 p-5">
              <h4 className="text-sm font-semibold text-brand-deep mb-4">地图板块信息（联系我们页）</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { key: 'address' as const, label: '门店地址' },
                  { key: 'phone' as const, label: '联系电话' },
                  { key: 'workingHours' as const, label: '营业时间' },
                ].map(({ key, label }) => (
                  <div key={key}>
                    <label className="block text-xs text-gray-400 mb-1.5">{label}</label>
                    <input
                      type="text"
                      value={config.mapInfo[key]}
                      onChange={(e) => update({ mapInfo: { ...config.mapInfo, [key]: e.target.value } })}
                      className="w-full px-3 py-2 text-sm text-brand-deep bg-brand-gray/50 rounded-lg border border-brand-light/30 focus:outline-none focus:border-brand-brown/40"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'modal' && (
          <div className="space-y-5">
            <div className="bg-white rounded-xl border border-brand-light/40 p-5">
              <h4 className="text-sm font-semibold text-brand-deep mb-4">预约体验弹窗</h4>
              <div className="mb-4">
                <ImageUploader
                  label="顾问微信二维码"
                  value={config.consultationModal.qrImage}
                  onChange={(v) => update({ consultationModal: { ...config.consultationModal, qrImage: v } })}
                  specHint="尺寸：600 × 600 px · 72 DPI · 正方形 · JPG/PNG · 不超过 2MB"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { key: 'phone' as const, label: '客服热线' },
                  { key: 'phoneLabel' as const, label: '热线标签' },
                  { key: 'hours' as const, label: '工作时间' },
                  { key: 'address' as const, label: '门店地址' },
                ].map(({ key, label }) => (
                  <div key={key}>
                    <label className="block text-xs text-gray-400 mb-1.5">{label}</label>
                    <input
                      type="text"
                      value={config.consultationModal[key]}
                      onChange={(e) => update({ consultationModal: { ...config.consultationModal, [key]: e.target.value } })}
                      className="w-full px-3 py-2 text-sm text-brand-deep bg-brand-gray/50 rounded-lg border border-brand-light/30 focus:outline-none focus:border-brand-brown/40"
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-xl border border-brand-light/40 p-5">
              <h4 className="text-sm font-semibold text-brand-deep mb-4">立即加盟弹窗</h4>
              <div className="mb-4">
                <ImageUploader
                  label="招商经理微信二维码"
                  value={config.franchiseModal.qrImage}
                  onChange={(v) => update({ franchiseModal: { ...config.franchiseModal, qrImage: v } })}
                  specHint="尺寸：600 × 600 px · 72 DPI · 正方形 · JPG/PNG · 不超过 2MB"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { key: 'phone' as const, label: '招商热线' },
                  { key: 'phoneLabel' as const, label: '热线标签' },
                  { key: 'hours' as const, label: '工作时间' },
                  { key: 'address' as const, label: '工作地址' },
                ].map(({ key, label }) => (
                  <div key={key}>
                    <label className="block text-xs text-gray-400 mb-1.5">{label}</label>
                    <input
                      type="text"
                      value={config.franchiseModal[key]}
                      onChange={(e) => update({ franchiseModal: { ...config.franchiseModal, [key]: e.target.value } })}
                      className="w-full px-3 py-2 text-sm text-brand-deep bg-brand-gray/50 rounded-lg border border-brand-light/30 focus:outline-none focus:border-brand-brown/40"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'footer' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <ImageUploader
              label="微信公众号二维码"
              value={config.footerQrWechat}
              onChange={(v) => update({ footerQrWechat: v })}
              hint="显示于底部栏"
              specHint="尺寸：600 × 600 px · 72 DPI · 正方形 · JPG/PNG · 不超过 2MB"
            />
            <ImageUploader
              label="抖音二维码"
              value={config.footerQrTiktok}
              onChange={(v) => update({ footerQrTiktok: v })}
              hint="显示于底部栏"
              specHint="尺寸：600 × 600 px · 72 DPI · 正方形 · JPG/PNG · 不超过 2MB"
            />
          </div>
        )}

        {activeTab === 'culture' && (
          <div className="space-y-5">
            <div className="bg-white rounded-xl border border-brand-light/40 p-5">
              <h4 className="text-sm font-semibold text-brand-deep mb-4">使命与愿景</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="bg-brand-gray/50 rounded-lg p-4 border border-brand-light/20">
                  <p className="text-xs font-semibold text-brand-deep mb-3 flex items-center gap-1.5">
                    <i className="ri-flag-2-line text-brand-brown" /> 使命
                  </p>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">标题</label>
                      <input
                        type="text"
                        value={config.corporateCulture.mission.title}
                        onChange={(e) => update({
                          corporateCulture: {
                            ...config.corporateCulture,
                            mission: { ...config.corporateCulture.mission, title: e.target.value },
                          },
                        })}
                        className="w-full px-3 py-2 text-sm text-brand-deep bg-white rounded-lg border border-brand-light/30 focus:outline-none focus:border-brand-brown/40"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">内容描述</label>
                      <textarea
                        value={config.corporateCulture.mission.description}
                        onChange={(e) => update({
                          corporateCulture: {
                            ...config.corporateCulture,
                            mission: { ...config.corporateCulture.mission, description: e.target.value },
                          },
                        })}
                        rows={4}
                        className="w-full px-3 py-2 text-sm text-brand-deep bg-white rounded-lg border border-brand-light/30 focus:outline-none focus:border-brand-brown/40 resize-none"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-brand-deep/5 rounded-lg p-4 border border-brand-deep/10">
                  <p className="text-xs font-semibold text-brand-deep mb-3 flex items-center gap-1.5">
                    <i className="ri-eye-2-line text-brand-gold" /> 愿景
                  </p>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">标题</label>
                      <input
                        type="text"
                        value={config.corporateCulture.vision.title}
                        onChange={(e) => update({
                          corporateCulture: {
                            ...config.corporateCulture,
                            vision: { ...config.corporateCulture.vision, title: e.target.value },
                          },
                        })}
                        className="w-full px-3 py-2 text-sm text-brand-deep bg-white rounded-lg border border-brand-light/30 focus:outline-none focus:border-brand-brown/40"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">内容描述</label>
                      <textarea
                        value={config.corporateCulture.vision.description}
                        onChange={(e) => update({
                          corporateCulture: {
                            ...config.corporateCulture,
                            vision: { ...config.corporateCulture.vision, description: e.target.value },
                          },
                        })}
                        rows={4}
                        className="w-full px-3 py-2 text-sm text-brand-deep bg-white rounded-lg border border-brand-light/30 focus:outline-none focus:border-brand-brown/40 resize-none"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-brand-light/40 p-5">
              <h4 className="text-sm font-semibold text-brand-deep mb-4">核心价值观</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {config.corporateCulture.values.map((value, i) => (
                  <div key={i} className="bg-brand-gray/50 rounded-lg p-4 border border-brand-light/20">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="w-5 h-5 flex items-center justify-center rounded bg-brand-deep text-white text-xs font-bold flex-shrink-0">{i + 1}</span>
                      <input
                        type="text"
                        value={value.title}
                        onChange={(e) => {
                          const next = config.corporateCulture.values.map((v, idx) =>
                            idx === i ? { ...v, title: e.target.value } : v
                          );
                          update({ corporateCulture: { ...config.corporateCulture, values: next } });
                        }}
                        className="flex-1 px-3 py-1.5 text-sm font-semibold text-brand-deep bg-white rounded-md border border-brand-light/30 focus:outline-none focus:border-brand-brown/40"
                        placeholder="价值观名称"
                      />
                    </div>
                    <div className="space-y-2">
                      <div>
                        <label className="block text-[10px] text-gray-400 mb-0.5">副标题</label>
                        <input
                          type="text"
                          value={value.subtitle}
                          onChange={(e) => {
                            const next = config.corporateCulture.values.map((v, idx) =>
                              idx === i ? { ...v, subtitle: e.target.value } : v
                            );
                            update({ corporateCulture: { ...config.corporateCulture, values: next } });
                          }}
                          className="w-full px-3 py-1.5 text-xs text-brand-brown bg-white rounded-md border border-brand-light/30 focus:outline-none focus:border-brand-brown/40"
                          placeholder="副标题"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-400 mb-0.5">描述文案</label>
                        <textarea
                          value={value.desc}
                          onChange={(e) => {
                            const next = config.corporateCulture.values.map((v, idx) =>
                              idx === i ? { ...v, desc: e.target.value } : v
                            );
                            update({ corporateCulture: { ...config.corporateCulture, values: next } });
                          }}
                          rows={3}
                          className="w-full px-3 py-1.5 text-xs text-gray-500 bg-white rounded-md border border-brand-light/30 focus:outline-none focus:border-brand-brown/40 resize-none"
                          placeholder="描述文案"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}